import { Box, ButtonBase, Dialog, SwipeableDrawer, Typography, makeStyles } from "@material-ui/core";
import React from "react";
import { useHistory } from "react-router";

import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    maxHeight: "90%",
    padding: "12px 24px calc(32px + env(safe-area-inset-bottom, 0px))",
    borderRadius: `${layoutTokens.radius.lg}px ${layoutTokens.radius.lg}px 0 0`,
    backgroundColor: semanticColors["surface/card"],
    boxShadow: "none",
  },
  pcPaper: {
    width: 480,
    maxWidth: "calc(100% - 64px)",
    padding: 32,
    borderRadius: layoutTokens.radius.lg,
  },
  backdrop: { backgroundColor: semanticColors["surface/overlay"] },
  handle: { display: "flex", justifyContent: "center", padding: "8px 0 12px", flexShrink: 0 },
  bar: { width: 36, height: 4, borderRadius: 2, backgroundColor: semanticColors["border/strong"] },
  title: {
    ...textStyles["SP/Heading/H3"],
    textAlign: "center",
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H3"] },
  },
  description: {
    ...textStyles["SP/Body/Small"],
    textAlign: "center",
    color: semanticColors["text/secondary"],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  head: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  group: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8], flexShrink: 0 },
  groupTitle: {
    ...textStyles["SP/Heading/H5"],
    color: semanticColors["text/heading"],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H5"] },
  },
  buttonContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    flexShrink: 0,
  },
  button: {
    width: "100%",
    justifyContent: "flex-start",
    padding: 12,
    borderRadius: layoutTokens.radius.md,
    backgroundColor: semanticColors["surface/base"],
    color: theme.palette.text.primary,
    textAlign: "left",
    "&:hover": { backgroundColor: semanticColors["state/selected-bg"] },
    "&.Mui-focusVisible": { outline: `2px solid ${semanticColors["border/focus"]}`, outlineOffset: 2 },
  },
  label: {
    ...textStyles["SP/Body/Small"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
}));

interface AddNewPropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  includeConsideration?: boolean;
}
export const AddNewPropertyModal: React.FC<AddNewPropertyModalProps> = ({ isOpen, onClose, includeConsideration = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const { isMobile } = useDevice();
  const selectProperty = (path: string) => {
    onClose();
    history.push(path);
  };

  const content = (
    <>
      {isMobile && <Box className={classes.handle} data-sheet-handle><span className={classes.bar} /></Box>}
      <Box className={classes.head}>
        <Typography component="h2" id="property-category-title" className={classes.title}>物件を登録</Typography>
        <Typography id="property-category-description" className={classes.description}>
          {includeConsideration ? "登録する物件を選んでください" : "登録する物件の種類を選んでください"}
        </Typography>
      </Box>
      <Box className={classes.group}>
        {includeConsideration && <Typography component="h3" className={classes.groupTitle}>保有している物件</Typography>}
        <Box className={classes.buttonContainer}>
          <ButtonBase className={classes.button} onClick={() => selectProperty("/add_new_property")}>
            <Typography component="span" className={classes.label}>区分マンション</Typography>
          </ButtonBase>
          <ButtonBase className={classes.button} onClick={() => selectProperty("/add_new_property/building")}>
            <Typography component="span" className={classes.label}>一棟物件</Typography>
          </ButtonBase>
        </Box>
      </Box>
      {includeConsideration && (
        <Box className={classes.group}>
          <Typography component="h3" className={classes.groupTitle}>検討している物件</Typography>
          <ButtonBase className={classes.button} onClick={() => selectProperty("/add_new_property/consideration")}>
            <Typography component="span" className={classes.label}>検討中の物件（区分マンションのみ）</Typography>
          </ButtonBase>
        </Box>
      )}
    </>
  );
  const accessibility = { "aria-labelledby": "property-category-title", "aria-describedby": "property-category-description" };
  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onOpen={() => {}}
      onClose={onClose}
      disableDiscovery
      disableSwipeToOpen
      PaperProps={{ className: classes.paper, role: "dialog", ...accessibility }}
      ModalProps={{ BackdropProps: { className: classes.backdrop } }}
    >
      {content}
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: `${classes.paper} ${classes.pcPaper}`, ...accessibility }}
      BackdropProps={{ className: classes.backdrop }}
    >
      {content}
    </Dialog>
  );
};
